import * as diff from "diff";

const buildInsertionMark = () => ({
  type: "insertion",
  attrs: {
    "data-op-user-id": "",
    "data-op-user-nickname": "",
    "data-op-date": "",
  },
});

function normalizeTextWithParagraphPolicy(text, policy = {}) {
  const {
    trimEdgeEmptyLines = false,
    maxConsecutiveInternalEmptyParagraphs = Infinity,
  } = policy;

  const rawLines = String(text ?? "").replace(/\r\n?/g, "\n").split("\n");
  let start = 0;
  let end = rawLines.length - 1;

  if (trimEdgeEmptyLines) {
    while (start <= end && rawLines[start].trim().length === 0) start += 1;
    while (end >= start && rawLines[end].trim().length === 0) end -= 1;
  }

  if (start > end) return "";

  const normalizedLines = [];
  let emptyLineRun = 0;

  const flushEmptyRun = ({ hasFollowingContent }) => {
    if (emptyLineRun === 0 || normalizedLines.length === 0) {
      emptyLineRun = 0;
      return;
    }

    if (!hasFollowingContent && trimEdgeEmptyLines) {
      emptyLineRun = 0;
      return;
    }

    normalizedLines.push("");

    if (emptyLineRun > 1) {
      const extraEmptyLines = emptyLineRun - 1;
      const allowedExtraEmptyLines = Math.min(extraEmptyLines, maxConsecutiveInternalEmptyParagraphs);
      for (let i = 0; i < allowedExtraEmptyLines; i += 1) {
        normalizedLines.push("");
      }
    }

    emptyLineRun = 0;
  };

  for (let i = start; i <= end; i += 1) {
    const line = rawLines[i];
    if (line.trim().length === 0) {
      emptyLineRun += 1;
      continue;
    }

    flushEmptyRun({ hasFollowingContent: true });
    normalizedLines.push(line);
  }

  flushEmptyRun({ hasFollowingContent: false });

  return normalizedLines.join("\n");
}

function buildParagraphFromLineSegments(lineSegments) {
  const content = [];

  lineSegments.forEach((segments, index) => {
    if (index > 0) {
      content.push({ type: "hardBreak" });
    }

    segments.forEach((segment) => {
      if (!segment.text) return;

      const node = { type: "text", text: segment.text };
      if (segment.markType === "insertion") {
        node.marks = [buildInsertionMark()];
      }

      const lastNode = content[content.length - 1];
      if (
        lastNode
        && lastNode.type === "text"
        && JSON.stringify(lastNode.marks) === JSON.stringify(node.marks)
      ) {
        lastNode.text += node.text;
      } else {
        content.push(node);
      }
    });
  });

  return { type: "paragraph", content };
}

function buildDocWithParagraphPolicy(changes, policy = {}) {
  const maxConsecutiveInternalEmptyParagraphs =
    policy.maxConsecutiveInternalEmptyParagraphs ?? Infinity;
  const lineSegments = [[]];

  const appendTextToCurrentLine = (text, markType) => {
    if (!text) return;
    const currentLine = lineSegments[lineSegments.length - 1];
    const lastSegment = currentLine[currentLine.length - 1];
    if (lastSegment && lastSegment.markType === markType) {
      lastSegment.text += text;
    } else {
      currentLine.push({ text, markType });
    }
  };

  changes.forEach((change) => {
    if (change.removed) return;
    const markType = change.added ? "insertion" : null;
    const parts = change.value.split("\n");
    parts.forEach((part, index) => {
      appendTextToCurrentLine(part, markType);
      if (index < parts.length - 1) {
        lineSegments.push([]);
      }
    });
  });

  const paragraphs = [];
  let currentParagraphLines = [];
  let emptyLineRun = 0;

  const flushCurrentParagraph = () => {
    if (currentParagraphLines.length === 0) return;
    paragraphs.push(buildParagraphFromLineSegments(currentParagraphLines));
    currentParagraphLines = [];
  };

  lineSegments.forEach((line) => {
    const isEmpty = line.length === 0;
    if (isEmpty) {
      flushCurrentParagraph();
      emptyLineRun += 1;
      return;
    }

    if (emptyLineRun > 1) {
      const extraEmptyParagraphs = emptyLineRun - 1;
      const allowedEmptyParagraphs = Math.min(extraEmptyParagraphs, maxConsecutiveInternalEmptyParagraphs);
      for (let i = 0; i < allowedEmptyParagraphs; i += 1) {
        paragraphs.push({ type: "paragraph", content: [] });
      }
    }
    emptyLineRun = 0;
    currentParagraphLines.push(line);
  });

  flushCurrentParagraph();

  return {
    type: "doc",
    content: paragraphs.length ? paragraphs : [{ type: "paragraph", content: [] }],
  };
}

export const diffToTiptap = (oldText = "", newText = "", options = {}) => {
  const paragraphPolicy = options?.normalizeParagraphSpacing;
  if (paragraphPolicy) {
    const normalizedOld = normalizeTextWithParagraphPolicy(oldText, paragraphPolicy);
    const normalizedNew = normalizeTextWithParagraphPolicy(newText, paragraphPolicy);
    const changes = diff.diffWords(normalizedOld, normalizedNew);
    return buildDocWithParagraphPolicy(changes, paragraphPolicy);
  }

  // If the texts are completely different (or one is empty and the other is not), treat it as a full replacement.
  // However, diffWords handles this naturally by returning a removal of oldText and addition of newText.
  // The user mentioned "You don't have to make a special case for replacing everything - it will automatically highlight the whole chapter summary if everything is different if the diff logic is sound".
  // So we trust diffWords.

  // But we need to ensure we handle the structure correctly.

  const changes = diff.diffWords(oldText, newText);

  const doc = { type: "doc", content: [] };
  let currentParagraph = { type: "paragraph", content: [] };

  changes.forEach(change => {
    // We only render unchanged text and insertions; deletions are omitted entirely.
    if (change.removed) {
      return;
    }
    const text = change.value;
    const type = change.added ? "insertion" : null;

    // Split text by newlines to handle paragraphs
    // We use a regex that matches one or more newlines to split paragraphs
    const parts = text.split(/\n\n+/);

    parts.forEach((part, index) => {
       if (index > 0) {
         if (currentParagraph.content.length > 0) {
            doc.content.push(currentParagraph);
         }
         currentParagraph = { type: "paragraph", content: [] };
       }

       if (part) {
         const lines = part.split(/\n/);
         lines.forEach((line, lineIndex) => {
            if (lineIndex > 0) {
                currentParagraph.content.push({ type: "hardBreak" });
            }
            if (line) {
                const node = { type: "text", text: line };
        if (type) {
          node.marks = [buildInsertionMark()];
        }

                // Try to merge with previous node if possible (same marks)
                const lastNode = currentParagraph.content[currentParagraph.content.length - 1];
                if (lastNode && lastNode.type === "text" &&
                    JSON.stringify(lastNode.marks) === JSON.stringify(node.marks)) {
                    lastNode.text += node.text;
                } else {
                    currentParagraph.content.push(node);
                }
            }
         });
       }
    });
  });

  if (currentParagraph.content.length > 0) {
    doc.content.push(currentParagraph);
  } else if (doc.content.length === 0) {
    // Ensure at least one paragraph
    doc.content.push({ type: "paragraph", content: [] });
  }

  return doc;
};

/** True if TipTap doc JSON carries track-change insertion/deletion marks (purple diff UI). */
export const tipTapDocJsonHasTrackMarks = (json) => {
  if (!json || typeof json !== "object") return false;

  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return false;
    for (const node of nodes) {
      if (!node || typeof node !== "object") continue;
      if (node.type === "text") {
        const marks = node.marks || [];
        if (marks.some((m) => m.type === "insertion" || m.type === "deletion")) return true;
      }
      if (node.content && walk(node.content)) return true;
    }
    return false;
  };

  return walk(json.content);
};

export const cleanTiptapDiffs = (json) => {
  if (!json || typeof json !== "object") return json;

  const cleanContent = (content) => {
    if (!Array.isArray(content)) return content;

    return content.reduce((acc, node) => {
      if (node.type === "text") {
        const marks = node.marks || [];
        const isDeleted = marks.some((m) => m.type === "deletion");

        if (isDeleted) {
          // Skip this node (it's deleted)
          return acc;
        }

        // Keep node but remove insertion mark
        const newMarks = marks.filter((m) => m.type !== "insertion");
        const newNode = { ...node };
        if (newMarks.length > 0) {
          newNode.marks = newMarks;
        } else {
          delete newNode.marks;
        }
        acc.push(newNode);
      } else {
        // Recursive for other nodes
        const newNode = { ...node };
        if (newNode.content) {
          newNode.content = cleanContent(newNode.content);
        }
        acc.push(newNode);
      }
      return acc;
    }, []);
  };

  return {
    ...json,
    content: cleanContent(json.content),
  };
};

/** Highlight the authoritative rich text, never put a Markdown summary in an editor.
 * O(nodes); text strings and their existing formatting are retained unchanged.
 */
export const highlightEntityField = (value, fieldKey, entityDiff, isPendingNew) => {
  const oldValue = entityDiff?.oldFields?.[fieldKey];
  const newValue = entityDiff?.newFields?.[fieldKey];
  if (!isPendingNew && (newValue === undefined || newValue === oldValue)) return null;
  if (!value || typeof value !== "object") return null;
  const highlight = (node) => ({
    ...node,
    ...(node.type === "text" ? { marks: [...(node.marks || []), buildInsertionMark()] } : {}),
    ...(node.content ? { content: node.content.map(highlight) } : {}),
  });
  return highlight(value);
};
